import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    ListItemText,
    Chip,
    OutlinedInput,
    Divider,
    CircularProgress
} from '@mui/material';
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DiscountIcon from '@mui/icons-material/Discount';
import PlansIcon from '@mui/icons-material/ListAlt';
import authService from '../Auth/AuthService'; // adjust import to your project

const discountTypes = ['FLAT', 'PERCENTAGE', 'REFERRAL'];

const EditCoupon = ({ initialData = null, onSave }) => {
    const [coupon, setCoupon] = useState({
        code: '',
        discountType: 'FLAT',        // FIX: string, not array
        discountAmount: '',
        discountPercentage: '',
        startDate: null,
        endDate: null,
        applicablePlans: [],
        isActive: true,
        oneTimePerUser: true,
        autoApply: false,
        maxUsageCount: ''
    });
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(false);

    useEffect(() => {
        const fetchPlans = async () => {
            setLoadingPlans(true);
            try {
                const url = authService.pathUrl('GET', '/subscription/plans'); // adjust endpoint
                const response = await authService.getAuth(url);
                setSubscriptionPlans(response);
            } catch (error) {
                console.error('Failed to load subscription plans:', error);
            } finally {
                setLoadingPlans(false);
            }
        };
        fetchPlans();
    }, []);

    useEffect(() => {
        if (initialData) {
            setCoupon({
                id: initialData.id,
                code: initialData.code || '',
                discountType: initialData.discountType || 'FLAT',
                discountAmount: initialData.discountAmount ?? '',
                discountPercentage: initialData.discountPercentage ?? '',
                startDate: initialData.startDate ? new Date(initialData.startDate) : null,
                endDate: initialData.endDate ? new Date(initialData.endDate) : null,
                applicablePlans: initialData.applicablePlans || initialData.planIds || [],
                isActive: initialData.isActive ?? true,
                oneTimePerUser: initialData.oneTimePerUser ?? true,
                autoApply: initialData.autoApply ?? false,
                maxUsageCount: initialData.maxUsageCount ?? ''
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCoupon((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleDateChange = (field, date) => {
        setCoupon((prev) => ({
            ...prev,
            [field]: date
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...coupon,
            planIds: coupon.applicablePlans.map((id) => parseInt(id)),
            startDate: coupon.startDate ? coupon.startDate.toISOString().split('T')[0] : null,
            endDate: coupon.endDate ? coupon.endDate.toISOString().split('T')[0] : null,
        };

        if (onSave) onSave(payload);
        // delete payload.applicablePlans;
    };

    if (loadingPlans) {
        return (
            <Box display="flex" justifyContent="center" p={5}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ maxWidth: 1100, mx: 'auto', p: 0 }}>
                <Card elevation={6} sx={{ borderRadius: 4, p: 3 }}>
                    <CardContent>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            <DiscountIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#1b1914' }} />
                            {initialData ? 'Edit Coupon' : 'Create Coupon'}
                        </Typography>
                        <Divider sx={{ mb: 5 }} />
                        <Box component="form" onSubmit={handleSubmit} mt={3}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Coupon Code"
                                        name="code"
                                        fullWidth
                                        required
                                        value={coupon.code}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Discount Type</InputLabel>
                                        <Select
                                            name="discountType"
                                            value={coupon.discountType}
                                            onChange={handleChange}
                                            label="Discount Type"
                                        >
                                            {discountTypes.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {coupon.discountType === 'PERCENTAGE' ? (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Discount Amount"
                                            name="discountAmount"
                                            type="number"
                                            fullWidth
                                            required
                                            value={coupon.discountAmount}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                ) : (
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Discount (%)"
                                            name="discountPercentage"
                                            type="number"
                                            fullWidth
                                            required
                                            value={coupon.discountPercentage}
                                            onChange={handleChange}
                                            inputProps={{ step: "any" }}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Max Usage Count (optional)"
                                        name="maxUsageCount"
                                        type="number"
                                        fullWidth
                                        value={coupon.maxUsageCount}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <DesktopDatePicker
                                        label="Start Date"
                                        value={coupon.startDate}
                                        onChange={(date) => handleDateChange('startDate', date)}
                                        renderInput={(params) => <TextField {...params} fullWidth />}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <DesktopDatePicker
                                        label="End Date"
                                        value={coupon.endDate}
                                        onChange={(date) => handleDateChange('endDate', date)}
                                        renderInput={(params) => <TextField {...params} fullWidth />}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>
                                            <PlansIcon sx={{ mr: 0.5 }} />
                                            Applicable Plans
                                        </InputLabel>
                                        <Select
                                            multiple
                                            name="applicablePlans"
                                            value={coupon.applicablePlans}
                                            onChange={handleChange}
                                            input={<OutlinedInput label="Applicable Plans" />}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((id) => {
                                                        const plan = subscriptionPlans.find((p) => p.id === id);
                                                        return <Chip key={id} label={plan?.name || id} color="info" />;
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            {subscriptionPlans.map((plan) => (
                                                <MenuItem key={plan.id} value={plan.id}>
                                                    <Checkbox checked={coupon.applicablePlans.includes(plan.id)} />
                                                    <ListItemText primary={plan.name} />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="isActive"
                                                checked={coupon.isActive}
                                                onChange={handleChange}
                                            />
                                        }
                                        label="Active"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="oneTimePerUser"
                                                checked={coupon.oneTimePerUser}
                                                onChange={handleChange}
                                            />
                                        }
                                        label="One Time Per User"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="autoApply"
                                                checked={coupon.autoApply}
                                                onChange={handleChange}
                                            />
                                        }
                                        label="Auto Apply"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        size="large"
                                        fullWidth
                                        sx={{
                                            borderRadius: '20px',
                                            backgroundColor: '#1b1914',
                                            '&:hover': { backgroundColor: '#503e02ff' }
                                        }}
                                    >
                                        Save Coupon
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </LocalizationProvider>
    );
};

export default EditCoupon;